import React, { memo } from 'react';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-common/src/async.tsx';
import {
	setPerformanceMarkLoadStart,
	setPerformanceMarkLoadEnd,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/performance-marks/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type ServicedeskSidebarType from './ui/index.tsx';
import { LazyForPaintServicedeskSidebar } from './async-for-paint.tsx';

const routesUsingLazyForPaint = new Set([
	'servicedesk-queues-custom-view',
	'servicedesk-practice-custom-view',
	'issue',
]);

// NOTE: using lazyForPaint instead of lazyAfterPaint caused #hot-108329 (in 2024), commit 58fc7e35c54e8cd4d545456e33ce85f53c830a42
export const LazyAfterPaintServicedeskSidebar = lazyAfterPaint<typeof ServicedeskSidebarType>(
	() => {
		setPerformanceMarkLoadStart();

		return import(/* webpackChunkName: "async-sidebar-servicedesk" */ './index').then((bundle) => {
			setPerformanceMarkLoadEnd();
			return bundle;
		});
	},
	{ entryFsSsrLiftImportToModule: false },
);

const AfterPaintServicedeskSidebar = memo(() => (
	<SidebarBoundary id="servicedeskProject">
		<LazyAfterPaintServicedeskSidebar />
	</SidebarBoundary>
));

const ForPaintServicedeskSidebar = memo(() => (
	<SidebarBoundary id="servicedeskProject">
		<LazyForPaintServicedeskSidebar />
	</SidebarBoundary>
));

const ServicedeskSidebar = memo(() => {
	const isLazyForPaintRoute = useIsActiveRoute(
		fg('queues_sidebar_lazyafterpaint_to_lazyforpaint') ? routesUsingLazyForPaint : 'issue',
	);

	return isLazyForPaintRoute ? <ForPaintServicedeskSidebar /> : <AfterPaintServicedeskSidebar />;
});

ServicedeskSidebar.displayName = 'ServicedeskSidebar';
ForPaintServicedeskSidebar.displayName = 'ServicedeskSidebar';

export { ForPaintServicedeskSidebar };
export default ServicedeskSidebar;
